import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    PlusIcon,
    TagIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';

import Layout from '@/components/Layout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { useI18n } from '@/hooks/useI18n';
import { apiFetch } from '@/utils/api';

interface UnitOfMeasure {
    id: string;
    name: string;
    abbreviation: string;
    unit_type: string;
    is_base_unit: boolean;
    conversion_factor?: number;
    base_unit_id?: string;
    alternative_names?: Record<string, string[]>;
    is_active: boolean;
    created_at: string;
    updated_at?: string;
}

interface UnitFormData {
    name: string;
    abbreviation: string;
    unit_type: string;
    is_base_unit: boolean;
    conversion_factor?: number;
    base_unit_id?: string;
}

const ALL_TYPES = '__all_types__';
const ALL_UNIT_KINDS = '__all_unit_kinds__';
const BASE_ONLY = '__base_only__';
const DERIVED_ONLY = '__derived_only__';
const NO_DEFAULT_UNIT = '__no_default_unit__';
const NO_BASE_UNIT = '__no_base_unit__';

const unitTypes = ['volume', 'weight', 'count', 'length'];

const emptyFormData: UnitFormData = {
    name: '',
    abbreviation: '',
    unit_type: 'volume',
    is_base_unit: false,
    conversion_factor: undefined,
    base_unit_id: undefined,
};

export default function UnitsPage() {
    const { t, isReady } = useI18n();
    const [units, setUnits] = useState<UnitOfMeasure[]>([]);
    const [baseUnits, setBaseUnits] = useState<UnitOfMeasure[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editingUnit, setEditingUnit] = useState<UnitOfMeasure | null>(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<string>(ALL_TYPES);
    const [filterBaseUnit, setFilterBaseUnit] = useState<string>(ALL_UNIT_KINDS);

    const [showAlternativeNames, setShowAlternativeNames] = useState<string | null>(null);
    const [editingAlternativeNames, setEditingAlternativeNames] = useState<string | null>(null);

    const [navMappings, setNavMappings] = useState<Record<string, string>>({});
    const [alternativeNamesForm, setAlternativeNamesForm] = useState<Record<string, string[]>>({});
    const [newAlternativeNameInputs, setNewAlternativeNameInputs] = useState<Record<string, string>>({});
    const [newSystemInput, setNewSystemInput] = useState('');

    const [defaultUnitId, setDefaultUnitId] = useState<string>(NO_DEFAULT_UNIT);
    const [savingSettings, setSavingSettings] = useState(false);

    const [formData, setFormData] = useState<UnitFormData>(emptyFormData);
    const [savingUnit, setSavingUnit] = useState(false);

    useEffect(() => {
        if (!isReady) {
            return;
        }
        void Promise.all([
            fetchUnits(),
            fetchBaseUnits(),
            fetchNavMappings(),
            fetchSettings(),
        ]);
    }, [isReady]);

    const fetchUnits = async () => {
        try {
            setLoading(true);
            const response = await apiFetch('/api/units/');
            if (response.ok) {
                const data = await response.json();
                setUnits(Array.isArray(data) ? data : []);
                return;
            }
            setError(t('units.errors.loadFailed', 'finance'));
        } catch (err) {
            setError(t('units.errors.loadFailed', 'finance'));
            console.error('Error fetching units:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchBaseUnits = async () => {
        try {
            const response = await apiFetch('/api/units/base-units');
            if (response.ok) {
                const data = await response.json();
                setBaseUnits(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Error fetching base units:', err);
        }
    };

    const fetchNavMappings = async () => {
        try {
            const response = await apiFetch('/api/units/nav-mappings');
            if (response.ok) {
                const data = await response.json();
                setNavMappings(data.nav_mappings || {});
            }
        } catch (err) {
            console.error('Error fetching NAV mappings:', err);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await apiFetch('/api/settings');
            if (response.ok) {
                const data = await response.json();
                const rawDefault = data.settings?.default_unit_of_measure_id;
                setDefaultUnitId(rawDefault || NO_DEFAULT_UNIT);
            }
        } catch (err) {
            console.error('Error fetching settings:', err);
        }
    };

    const saveDefaultUnit = async () => {
        try {
            setSavingSettings(true);
            const selectedDefault = defaultUnitId === NO_DEFAULT_UNIT ? null : defaultUnitId;
            const response = await apiFetch('/api/settings', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    settings: {
                        default_unit_of_measure_id: selectedDefault,
                    },
                }),
            });

            if (response.ok) {
                toast.success(t('units.toasts.defaultSaved', 'finance'));
                return;
            }

            const errorData = await response.json();
            setError(errorData.detail || t('units.errors.saveDefaultFailed', 'finance'));
        } catch (err) {
            setError(t('units.errors.saveDefaultFailed', 'finance'));
            console.error('Error saving default unit:', err);
        } finally {
            setSavingSettings(false);
        }
    };

    const resetForm = () => {
        setFormData(emptyFormData);
        setEditingUnit(null);
        setShowForm(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.abbreviation.trim()) {
            setError(t('units.errors.nameAndAbbreviationRequired', 'finance'));
            return;
        }

        if (!formData.is_base_unit) {
            if (!formData.base_unit_id) {
                setError(t('units.errors.baseUnitRequired', 'finance'));
                return;
            }
            if (!formData.conversion_factor || formData.conversion_factor <= 0) {
                setError(t('units.errors.validConversionFactorRequired', 'finance'));
                return;
            }
        }

        try {
            setSavingUnit(true);
            const url = editingUnit ? `/api/units/${editingUnit.id}` : '/api/units/';
            const method = editingUnit ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                await Promise.all([fetchUnits(), fetchBaseUnits()]);
                resetForm();
                toast.success(
                    editingUnit
                        ? t('units.toasts.unitUpdated', 'finance')
                        : t('units.toasts.unitCreated', 'finance')
                );
                return;
            }

            const errorData = await response.json();
            setError(errorData.detail || t('units.errors.saveUnitFailed', 'finance'));
        } catch (err) {
            setError(t('units.errors.saveUnitFailed', 'finance'));
            console.error('Error saving unit:', err);
        } finally {
            setSavingUnit(false);
        }
    };

    const handleEdit = (unit: UnitOfMeasure) => {
        setEditingUnit(unit);
        setFormData({
            name: unit.name,
            abbreviation: unit.abbreviation,
            unit_type: unit.unit_type,
            is_base_unit: unit.is_base_unit,
            conversion_factor: unit.conversion_factor,
            base_unit_id: unit.base_unit_id,
        });
        setShowForm(true);
    };

    const handleDelete = async (unit: UnitOfMeasure) => {
        if (!confirm(t('units.confirmDelete', 'finance', { name: unit.name }))) {
            return;
        }

        try {
            const response = await apiFetch(`/api/units/${unit.id}`, { method: 'DELETE' });
            if (response.ok) {
                await Promise.all([fetchUnits(), fetchBaseUnits()]);
                toast.success(t('units.toasts.unitDeleted', 'finance'));
                return;
            }

            const errorData = await response.json();
            setError(errorData.detail || t('units.errors.deleteUnitFailed', 'finance'));
        } catch (err) {
            setError(t('units.errors.deleteUnitFailed', 'finance'));
            console.error('Error deleting unit:', err);
        }
    };

    const getBaseUnitsByType = (unitType: string) => {
        return baseUnits.filter((unit) => unit.unit_type === unitType);
    };

    const handleEditAlternativeNames = (unit: UnitOfMeasure) => {
        setEditingAlternativeNames(unit.id);
        setAlternativeNamesForm(unit.alternative_names || {});
        setNewAlternativeNameInputs({});
        setNewSystemInput('');
    };

    const handleSaveAlternativeNames = async () => {
        if (!editingAlternativeNames) {
            return;
        }

        try {
            const response = await apiFetch(`/api/units/${editingAlternativeNames}/alternative-names`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(alternativeNamesForm),
            });

            if (response.ok) {
                await Promise.all([fetchUnits(), fetchNavMappings()]);
                setEditingAlternativeNames(null);
                setAlternativeNamesForm({});
                setNewAlternativeNameInputs({});
                setNewSystemInput('');
                toast.success(t('units.toasts.alternativeNamesUpdated', 'finance'));
                return;
            }

            const errorData = await response.json();
            setError(errorData.detail || t('units.errors.saveAlternativeNamesFailed', 'finance'));
        } catch (err) {
            setError(t('units.errors.saveAlternativeNamesFailed', 'finance'));
            console.error('Error saving alternative names:', err);
        }
    };

    const addAlternativeName = (system: string) => {
        const rawValue = newAlternativeNameInputs[system] || '';
        const trimmedValue = rawValue.trim();
        if (!trimmedValue) {
            return;
        }

        setAlternativeNamesForm((prev) => {
            const existing = prev[system] || [];
            if (existing.includes(trimmedValue)) {
                return prev;
            }
            return {
                ...prev,
                [system]: [...existing, trimmedValue],
            };
        });

        setNewAlternativeNameInputs((prev) => ({ ...prev, [system]: '' }));
    };

    const removeAlternativeName = (system: string, index: number) => {
        setAlternativeNamesForm((prev) => ({
            ...prev,
            [system]: (prev[system] || []).filter((_, itemIndex) => itemIndex !== index),
        }));
    };

    const addAlternativeSystem = () => {
        const normalized = newSystemInput.trim().toUpperCase();
        if (!normalized || alternativeNamesForm[normalized]) {
            return;
        }

        setAlternativeNamesForm((prev) => ({
            ...prev,
            [normalized]: [],
        }));
        setNewSystemInput('');
    };

    const removeAlternativeSystem = (system: string) => {
        setAlternativeNamesForm((prev) => {
            const updated = { ...prev };
            delete updated[system];
            return updated;
        });
        setNewAlternativeNameInputs((prev) => {
            const updated = { ...prev };
            delete updated[system];
            return updated;
        });
    };

    const filteredUnits = useMemo(() => {
        return units.filter((unit) => {
            const matchesSearch = unit.name.toLowerCase().includes(searchTerm.toLowerCase())
                || unit.abbreviation.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesType = filterType === ALL_TYPES || unit.unit_type === filterType;

            const matchesBaseUnit = filterBaseUnit === ALL_UNIT_KINDS
                || (filterBaseUnit === BASE_ONLY && unit.is_base_unit)
                || (filterBaseUnit === DERIVED_ONLY && !unit.is_base_unit);

            return matchesSearch && matchesType && matchesBaseUnit;
        });
    }, [units, searchTerm, filterType, filterBaseUnit]);

    if (!isReady || loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg">{t('actions.loading', 'common')}</div>
                </div>
            </Layout>
        );
    }

    const selectedAlternativeNamesUnit = units.find((unit) => unit.id === showAlternativeNames) || null;
    const editingAlternativeNamesUnit = units.find((unit) => unit.id === editingAlternativeNames) || null;

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{t('units.title', 'finance')}</h1>
                        <p className="text-gray-600">{t('units.description', 'finance')}</p>
                    </div>
                    <Button onClick={() => setShowForm(true)}>
                        <PlusIcon className="h-4 w-4" />
                        {t('units.actions.addUnit', 'finance')}
                    </Button>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <ExclamationTriangleIcon className="h-4 w-4" />
                        <AlertDescription className="flex items-center justify-between gap-3">
                            <span>{error}</span>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-auto px-2 py-1 text-destructive hover:text-destructive"
                                onClick={() => setError(null)}
                            >
                                {t('actions.close', 'common')}
                            </Button>
                        </AlertDescription>
                    </Alert>
                )}

                <div className="space-y-4">
                    <div className="border rounded-lg p-4 space-y-4">
                        <div>
                            <h2 className="text-lg font-semibold">{t('units.defaultUnit.title', 'finance')}</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {t('units.defaultUnit.description', 'finance')}
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 md:flex-row md:items-center">
                            <div className="w-full md:max-w-md">
                                <Select
                                    value={defaultUnitId}
                                    onValueChange={setDefaultUnitId}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={t('units.defaultUnit.placeholder', 'finance')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={NO_DEFAULT_UNIT}>{t('units.defaultUnit.none', 'finance')}</SelectItem>
                                        {unitTypes.flatMap((type) =>
                                            units
                                                .filter((unit) => unit.unit_type === type)
                                                .map((unit) => (
                                                    <SelectItem key={unit.id} value={unit.id}>
                                                        {t(`units.types.${type}`, 'finance')}: {unit.abbreviation} - {unit.name}
                                                    </SelectItem>
                                                ))
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button onClick={saveDefaultUnit} disabled={savingSettings}>
                                {savingSettings ? t('actions.saving', 'common') : t('units.defaultUnit.save', 'finance')}
                            </Button>
                        </div>
                    </div>

                    <div className="border rounded-lg p-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            <div className="relative">
                                <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={t('units.filters.searchPlaceholder', 'finance')}
                                    className="pl-9"
                                />
                            </div>

                            <Select value={filterType} onValueChange={setFilterType}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder={t('units.filters.typePlaceholder', 'finance')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL_TYPES}>{t('units.filters.allTypes', 'finance')}</SelectItem>
                                    {unitTypes.map((type) => (
                                        <SelectItem key={type} value={type}>
                                            {t(`units.types.${type}`, 'finance')}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <Select value={filterBaseUnit} onValueChange={setFilterBaseUnit}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder={t('units.filters.unitKindPlaceholder', 'finance')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL_UNIT_KINDS}>{t('units.filters.allUnits', 'finance')}</SelectItem>
                                    <SelectItem value={BASE_ONLY}>{t('units.filters.baseOnly', 'finance')}</SelectItem>
                                    <SelectItem value={DERIVED_ONLY}>{t('units.filters.derivedOnly', 'finance')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('units.table.unit', 'finance')}</TableHead>
                                    <TableHead>{t('units.table.type', 'finance')}</TableHead>
                                    <TableHead>{t('units.table.baseUnit', 'finance')}</TableHead>
                                    <TableHead>{t('units.table.conversion', 'finance')}</TableHead>
                                    <TableHead>{t('units.table.alternativeNames', 'finance')}</TableHead>
                                    <TableHead>{t('units.table.status', 'finance')}</TableHead>
                                    <TableHead className="text-right">{t('actions.actions', 'common')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredUnits.map((unit) => (
                                    <TableRow key={unit.id}>
                                        <TableCell>
                                            <div className="font-medium">{unit.name}</div>
                                            <div className="text-xs text-muted-foreground">{unit.abbreviation}</div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{t(`units.types.${unit.unit_type}`, 'finance')}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            {unit.is_base_unit ? (
                                                <Badge>{t('units.badges.baseUnit', 'finance')}</Badge>
                                            ) : (
                                                <Badge variant="secondary">{t('units.badges.derived', 'finance')}</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>{unit.conversion_factor ? `${unit.conversion_factor}x` : '-'}</TableCell>
                                        <TableCell>
                                            {unit.alternative_names && Object.keys(unit.alternative_names).length > 0 ? (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setShowAlternativeNames(unit.id)}
                                                >
                                                    {t('actions.view', 'common')} ({Object.values(unit.alternative_names).flat().length})
                                                </Button>
                                            ) : (
                                                <span className="text-sm text-muted-foreground">{t('messages.none', 'common')}</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={unit.is_active ? 'default' : 'secondary'}>
                                                {unit.is_active ? t('status.active', 'common') : t('status.inactive', 'common')}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleEdit(unit)}
                                                    title={t('units.actions.editUnit', 'finance')}
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setShowAlternativeNames(unit.id)}
                                                    title={t('units.actions.viewAlternativeNames', 'finance')}
                                                >
                                                    <TagIcon className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDelete(unit)}
                                                    title={t('actions.delete', 'common')}
                                                    className="text-red-600 hover:text-red-600"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredUnits.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                                            {t('units.table.empty', 'finance')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            <Dialog
                open={showForm}
                onOpenChange={(open) => {
                    if (!open) {
                        resetForm();
                    } else {
                        setShowForm(true);
                    }
                }}
            >
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingUnit ? t('units.actions.editUnit', 'finance') : t('units.actions.addNewUnit', 'finance')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('units.form.dialogDescription', 'finance')}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="unit-name">{t('units.form.name', 'finance')}</Label>
                                <Input
                                    id="unit-name"
                                    value={formData.name}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                                    placeholder={t('units.form.namePlaceholder', 'finance')}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="unit-abbreviation">{t('units.form.abbreviation', 'finance')}</Label>
                                <Input
                                    id="unit-abbreviation"
                                    value={formData.abbreviation}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, abbreviation: e.target.value }))}
                                    placeholder={t('units.form.abbreviationPlaceholder', 'finance')}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="unit-type">{t('units.form.unitType', 'finance')}</Label>
                                <Select
                                    value={formData.unit_type}
                                    onValueChange={(value) => setFormData((prev) => ({ ...prev, unit_type: value }))}
                                >
                                    <SelectTrigger className="w-full" id="unit-type">
                                        <SelectValue placeholder={t('units.form.unitTypePlaceholder', 'finance')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {unitTypes.map((type) => (
                                            <SelectItem key={type} value={type}>
                                                {t(`units.types.${type}`, 'finance')}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex items-center space-x-2 md:pt-8">
                                <Checkbox
                                    id="is-base-unit"
                                    checked={formData.is_base_unit}
                                    onCheckedChange={(checked) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            is_base_unit: Boolean(checked),
                                            conversion_factor: checked ? undefined : prev.conversion_factor,
                                            base_unit_id: checked ? undefined : prev.base_unit_id,
                                        }))
                                    }
                                />
                                <Label htmlFor="is-base-unit">{t('units.form.isBaseUnit', 'finance')}</Label>
                            </div>

                            {!formData.is_base_unit && (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="base-unit">{t('units.form.baseUnit', 'finance')}</Label>
                                        <Select
                                            value={formData.base_unit_id || NO_BASE_UNIT}
                                            onValueChange={(value) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    base_unit_id: value === NO_BASE_UNIT ? undefined : value,
                                                }))
                                            }
                                        >
                                            <SelectTrigger className="w-full" id="base-unit">
                                                <SelectValue placeholder={t('units.form.baseUnitPlaceholder', 'finance')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={NO_BASE_UNIT}>{t('units.form.baseUnitPlaceholder', 'finance')}</SelectItem>
                                                {getBaseUnitsByType(formData.unit_type).map((unit) => (
                                                    <SelectItem key={unit.id} value={unit.id}>
                                                        {unit.name} ({unit.abbreviation})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="conversion-factor">{t('units.form.conversionFactor', 'finance')}</Label>
                                        <Input
                                            id="conversion-factor"
                                            type="number"
                                            step="0.000001"
                                            value={formData.conversion_factor ?? ''}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    conversion_factor: e.target.value
                                                        ? parseFloat(e.target.value)
                                                        : undefined,
                                                }))
                                            }
                                            placeholder={t('units.form.conversionFactorPlaceholder', 'finance')}
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            {t('units.form.conversionFactorHint', 'finance')}
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={resetForm}>
                                {t('actions.cancel', 'common')}
                            </Button>
                            <Button type="submit" disabled={savingUnit}>
                                {savingUnit
                                    ? t('actions.saving', 'common')
                                    : editingUnit
                                        ? t('actions.update', 'common')
                                        : t('actions.create', 'common')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={Boolean(showAlternativeNames)} onOpenChange={(open) => !open && setShowAlternativeNames(null)}>
                <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
                    {selectedAlternativeNamesUnit && (
                        <>
                            <DialogHeader>
                                <DialogTitle>
                                    {t('units.alternativeNames.viewTitle', 'finance', { name: selectedAlternativeNamesUnit.name })}
                                </DialogTitle>
                                <DialogDescription>
                                    {t('units.alternativeNames.viewDescription', 'finance')}
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-4">
                                {selectedAlternativeNamesUnit.alternative_names
                                && Object.keys(selectedAlternativeNamesUnit.alternative_names).length > 0 ? (
                                        Object.entries(selectedAlternativeNamesUnit.alternative_names).map(([system, names]) => (
                                            <div key={system} className="border rounded-md p-3 space-y-2">
                                                <div className="font-medium">
                                                    {t('units.alternativeNames.systemTitle', 'finance', { system })}
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {names.map((name) => (
                                                        <Badge key={`${system}-${name}`} variant="outline">
                                                            {name}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-muted-foreground">{t('units.alternativeNames.noNames', 'finance')}</p>
                                    )}

                                <div className="border-t pt-4">
                                    <div className="font-medium mb-1">{t('units.alternativeNames.navStatusTitle', 'finance')}</div>
                                    {Object.entries(navMappings).some(([, unitName]) => unitName === selectedAlternativeNamesUnit.name) ? (
                                        <p className="text-sm text-green-700">
                                            {t('units.alternativeNames.navMappedCodes', 'finance')}{' '}
                                            {Object.entries(navMappings)
                                                .filter(([, unitName]) => unitName === selectedAlternativeNamesUnit.name)
                                                .map(([navCode]) => navCode)
                                                .join(', ')}
                                        </p>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">{t('units.alternativeNames.navNoMappings', 'finance')}</p>
                                    )}
                                </div>
                            </div>

                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        handleEditAlternativeNames(selectedAlternativeNamesUnit);
                                        setShowAlternativeNames(null);
                                    }}
                                >
                                    {t('units.actions.editNames', 'finance')}
                                </Button>
                                <Button type="button" onClick={() => setShowAlternativeNames(null)}>
                                    {t('actions.close', 'common')}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={Boolean(editingAlternativeNames)}
                onOpenChange={(open) => {
                    if (!open) {
                        setEditingAlternativeNames(null);
                        setAlternativeNamesForm({});
                        setNewAlternativeNameInputs({});
                        setNewSystemInput('');
                    }
                }}
            >
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    {editingAlternativeNamesUnit && (
                        <>
                            <DialogHeader>
                                <DialogTitle>
                                    {t('units.alternativeNames.editTitle', 'finance', { name: editingAlternativeNamesUnit.name })}
                                </DialogTitle>
                                <DialogDescription>
                                    {t('units.alternativeNames.editDescription', 'finance')}
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-4">
                                {Object.keys(alternativeNamesForm).length === 0 && (
                                    <p className="text-sm text-muted-foreground">{t('units.alternativeNames.noSystemsYet', 'finance')}</p>
                                )}

                                {Object.entries(alternativeNamesForm).map(([system, names]) => (
                                    <div key={system} className="border rounded-md p-3 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <h4 className="font-medium">{t('units.alternativeNames.systemTitle', 'finance', { system })}</h4>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-600 hover:text-red-600"
                                                onClick={() => removeAlternativeSystem(system)}
                                            >
                                                {t('units.actions.removeSystem', 'finance')}
                                            </Button>
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            {names.length > 0 ? (
                                                names.map((name, index) => (
                                                    <Badge key={`${system}-${name}-${index}`} variant="outline" className="gap-1">
                                                        {name}
                                                        <button
                                                            type="button"
                                                            onClick={() => removeAlternativeName(system, index)}
                                                            className="ml-1 text-muted-foreground hover:text-foreground"
                                                        >
                                                            ×
                                                        </button>
                                                    </Badge>
                                                ))
                                            ) : (
                                                <span className="text-sm text-muted-foreground">{t('units.alternativeNames.noNamesYet', 'finance')}</span>
                                            )}
                                        </div>

                                        <div className="flex gap-2">
                                            <Input
                                                value={newAlternativeNameInputs[system] || ''}
                                                placeholder={t('units.alternativeNames.newNamePlaceholder', 'finance')}
                                                onChange={(e) =>
                                                    setNewAlternativeNameInputs((prev) => ({
                                                        ...prev,
                                                        [system]: e.target.value,
                                                    }))
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addAlternativeName(system);
                                                    }
                                                }}
                                            />
                                            <Button type="button" variant="outline" onClick={() => addAlternativeName(system)}>
                                                {t('units.actions.add', 'finance')}
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                <div className="border-2 border-dashed rounded-md p-3 space-y-2">
                                    <Label htmlFor="new-system-name">{t('units.alternativeNames.addNewSystem', 'finance')}</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            id="new-system-name"
                                            value={newSystemInput}
                                            placeholder={t('units.alternativeNames.newSystemPlaceholder', 'finance')}
                                            onChange={(e) => setNewSystemInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addAlternativeSystem();
                                                }
                                            }}
                                        />
                                        <Button type="button" variant="outline" onClick={addAlternativeSystem}>
                                            {t('units.actions.addSystem', 'finance')}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setEditingAlternativeNames(null);
                                        setAlternativeNamesForm({});
                                        setNewAlternativeNameInputs({});
                                        setNewSystemInput('');
                                    }}
                                >
                                    {t('actions.cancel', 'common')}
                                </Button>
                                <Button type="button" onClick={handleSaveAlternativeNames}>
                                    <CheckCircleIcon className="h-4 w-4" />
                                    {t('actions.saveChanges', 'common')}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

export const Route = createFileRoute('/units')({
    component: UnitsPage,
});
